import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusBadge from './StatusBadge';
import UrgencyBadge from './UrgencyBadge';
import CaseWorkflowProgress from '../../../components/cases/CaseWorkflowProgress';
import {
  getCaseAssistantPath,
  shouldOpenCaseViaAssistant,
} from '../../../lib/dentistSubmissionWizard';

const CaseMobileCard = ({
  caseData,
  isSelected,
  onSelect,
  isAdmin = false,
  onAssignProsthetist,
  onAssignProductionStaff,
  dentistClientView = false,
  viewerRole = null,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleViewCase = () => {
    if (!caseData?.id) return;
    if (
      shouldOpenCaseViaAssistant(
        caseData.id,
        caseData.timelinePhases,
        viewerRole,
      )
    ) {
      navigate(getCaseAssistantPath(caseData.id));
      return;
    }
    navigate(`/case-detail/${caseData.id}`);
  };

  const handleViewPatient = (e) => {
    e?.stopPropagation?.();
    if (caseData?.patientId) navigate(`/patient-profile/${caseData.patientId}`);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleViewCase}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleViewCase();
        }
      }}
      className="bg-card border border-border rounded-lg p-4 mb-3 cursor-pointer transition-colors duration-200 hover:bg-muted/70 dark:hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="flex items-start gap-3 mb-3">
        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(caseData?.id, e?.target?.checked)}
          />
        </span>
        <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
          <Image
            src={caseData?.patientAvatar}
            alt={caseData?.patientAvatarAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={handleViewPatient}
            className="font-semibold text-foreground hover:text-primary transition-colors duration-200 block truncate text-left w-full"
          >
            {caseData?.patientName}
          </button>
          <p className="text-xs text-muted-foreground">{caseData?.caseId}</p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-1 hover:bg-muted rounded transition-colors duration-200"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Masquer les détails' : 'Afficher les détails'}
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <StatusBadge status={caseData?.status} />
        <UrgencyBadge level={caseData?.urgency} />
        {dentistClientView ? (
          <div className="w-full mt-1">
            <CaseWorkflowProgress
              variant="compact"
              percent={caseData?.workflowProgress?.percent ?? 0}
              activeGroupNum={caseData?.workflowProgress?.activeGroupNum ?? 1}
              totalLots={caseData?.workflowProgress?.totalLots ?? 1}
              showMacroLabel={false}
              className="max-w-full"
            />
          </div>
        ) : null}
        {caseData?.incidentCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-error/10 text-error border border-error/20">
            <Icon name="AlertCircle" size={12} />
            {caseData?.incidentCount} incidents
          </span>
        )}
      </div>
      {isExpanded && (
        <div className="space-y-3 pt-3 border-t border-border">
          {!dentistClientView && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Dentiste client:</span>
                <span className="font-medium text-foreground">
                  {caseData?.dentistName || 'Non assigné'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Personnel assigné:</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-muted">
                    <Image
                      src={caseData?.assignedStaffAvatar}
                      alt={caseData?.assignedStaffAvatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-medium text-foreground">{caseData?.assignedStaff}</span>
                </div>
              </div>
            </>
          )}
          {!dentistClientView ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Phase:</span>
              <span className="font-medium text-foreground">{caseData?.phase}</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground shrink-0">Pack:</span>
            <span className="font-medium text-foreground text-right min-w-0 break-words">
              {caseData?.packageLabel ?? '—'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Dernière activité:</span>
            <span className="font-medium text-foreground">{formatDate(caseData?.lastActivity)}</span>
          </div>
        </div>
      )}
      <div
        className="flex items-center gap-2 mt-3 pt-3 border-t border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={handleViewCase}
          fullWidth
        >
          Voir
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="MessageSquare"
          iconPosition="left"
          onClick={handleViewCase}
          fullWidth
        >
          Message
        </Button>
        {isAdmin && (
          <>
            <Button
              variant="outline"
              size="sm"
              iconName="UserCog"
              iconPosition="left"
              onClick={() => onAssignProsthetist && onAssignProsthetist(caseData?.id)}
            >
              Dentiste
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Users"
              iconPosition="left"
              onClick={() => onAssignProductionStaff && onAssignProductionStaff(caseData?.id)}
            >
              Prod/Dist
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CaseMobileCard;